import React from 'react';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { showToast } from 'stores/ToastStore';
import CapabilityCircle from 'components/modules/user/CapabilityCircle';
import formatMoney from 'utils/formatters/Money';
import { getShipmentDetailUrl, getLocation, getLoad, getEquipment, } from 'utils/formatters/shipments';
import { getDocumentsV2, getSignedUrl } from '../../../utils/DocumentsAPIUtils';
import { getShipment } from '../../../utils/ShipmentWebAPIUtils';
import { useTranslation } from 'react-i18next';
const VALUE_TYPES = {
    DATE: 'DATE',
    EQUIPMENT: 'EQUIPMENT',
    LINK: 'LINK',
    VERIFY_ICON: 'VERIFY_ICON',
    LOCATION: 'LOCATION',
    LOAD: 'LOAD',
    MONEY: 'MONEY',
    POD: 'POD',
    DEFAULT: 'DEFAULT',
};
const FORMATTERS = {
    DATE: (value) => moment(value).format('L'),
    EQUIPMENT: getEquipment,
    LINK: (valueId, displayValue = '') => {
        return (React.createElement(Link, { to: getShipmentDetailUrl(valueId), target: "_blank" }, displayValue || valueId));
    },
    LOAD: getLoad,
    LOCATION: getLocation,
    MONEY: (value, currency) => formatMoney(value, currency),
    POD: (value) => getPODLink(value),
    VERIFY_ICON: (verified, message = 'Pending') => {
        if (verified) {
            return React.createElement(CapabilityCircle, { checked: verified });
        }
        return React.createElement("span", { "data-testid": "span-f8428f44" }, message);
    },
    DEFAULT: (value) => value,
};
const getValue = (value, type, displayValue = '', currency) => {
    switch (type) {
        case VALUE_TYPES.DATE:
            return value ? FORMATTERS.DATE(value) : '';
        case VALUE_TYPES.EQUIPMENT:
            return value ? FORMATTERS.EQUIPMENT(value) : '';
        case VALUE_TYPES.LINK:
            return value ? FORMATTERS.LINK(value, displayValue) : '';
        case VALUE_TYPES.LOAD:
            return value ? FORMATTERS.LOAD(value) : '';
        case VALUE_TYPES.LOCATION:
            return value ? FORMATTERS.LOCATION(value) : '';
        case VALUE_TYPES.MONEY:
            return value
                ? FORMATTERS.MONEY(value, currency)
                : FORMATTERS.MONEY(0, currency);
        case VALUE_TYPES.POD:
            return value ? FORMATTERS.POD(value) : '';
        case VALUE_TYPES.VERIFY_ICON:
            return value ? FORMATTERS.VERIFY_ICON(value) : '';
        default:
            return FORMATTERS.DEFAULT(value);
    }
};
const getPODLink = (shipment_id) => {
    const { t } = useTranslation();
    return ((React.createElement("a", { "data-testid": "a-9f712ecc", onClick: (event) => handlePODClick(shipment_id, t, event), target: "_blank", rel: "noreferrer" }, t('POD'))));
};
const handlePODClick = async (shipment_id, t, event) => {
    const podKey = await getPOD(shipment_id, t);
    if (podKey) {
        const open = preparePODOpen(podKey);
        open(event);
    }
};
const preparePODOpen = (podKey) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const windowRef = window.open();
    openPOD(podKey, windowRef);
};
const openPOD = async (pod, windowRef) => {
    var _a;
    try {
        const { data: signedUrl } = await getSignedUrl(pod);
        windowRef.location = (_a = signedUrl === null || signedUrl === void 0 ? void 0 : signedUrl.getSignedUrl) === null || _a === void 0 ? void 0 : _a.signedUrl;
    }
    catch (e) {
        console.error('job-history: error getting signed URL:', e);
    }
};
const getPOD = async (shipment_id, t) => {
    var _a;
    try {
        const { entity: { item: shipment }, } = await getShipment(shipment_id);
        if (!shipment) {
            throw new Error('Error Fetching Shipment');
        }
        const pickupAttempts = shipment.stops.filter((s) => s.attempt && s.action === 'pickup');
        const delivery = _.sortBy(shipment.stops, 'sequence').find((s) => s.action === 'deliver' && !s.empty);
        if (pickupAttempts.length || (delivery && delivery.attempt)) {
            return 'Attempt';
        }
        if (delivery) {
            const { data } = await getDocumentsV2([delivery.id], 'proof_of_delivery', {
                order: {
                    field: 'createdAt',
                    direction: 'desc',
                },
                limit: 1,
            });
            const pod = (_a = data === null || data === void 0 ? void 0 : data.getDocumentsV2) === null || _a === void 0 ? void 0 : _a.documents[0];
            if (pod) {
                return pod.key;
            }
        }
        showToast(t('SHIPMENT_MISSING_POD'), null, 'error');
    }
    catch (e) {
        console.error(e);
        showToast(t('ERROR_FETCHING_DOCUMENTS'), null, 'error');
    }
};
const JobPODColumn = ({ job }) => {
    return getPODLink(job.shipment_id);
};
const SHORTHAUL_COLUMNS = [
    // deliver_by, shipment_id, pod verififcation, origin, destination, load, weight, driver, pod, total, closed date
    {
        title: 'DELIVER_BY',
        field: 'jobDeliverBy',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
    {
        title: 'SHIPMENT_ID',
        field: 'shipment_id',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LINK, item.shipmentId);
        },
    },
    {
        title: 'POD_VERIFICATION',
        field: 'is_pod_valid',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.VERIFY_ICON);
        },
    },
    {
        title: 'ORIGIN',
        titleize: true,
        field: 'origin',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LOCATION);
        },
    },
    {
        title: 'DESTINATION',
        titleize: true,
        field: 'destination',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LOCATION);
        },
    },
    {
        title: 'LOAD',
        field: 'loads',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LOAD);
        },
    },
    {
        title: 'WEIGHT',
        titleize: true,
        field: 'weight',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DEFAULT);
        },
    },
    {
        title: 'DRIVER',
        titleize: true,
        count: 1,
        field: 'driverName',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DEFAULT);
        },
    },
    {
        title: 'TOTAL',
        field: 'total',
        value(_item, total) {
            var _a;
            return getValue(total, VALUE_TYPES.MONEY, (_a = _item.lineItems[0]) === null || _a === void 0 ? void 0 : _a.currency);
        },
    },
    {
        title: 'POD',
        field: 'pod',
        value(item) {
            return React.createElement(JobPODColumn, { job: item });
        },
    },
    {
        title: 'CLOSED_DATE',
        field: 'jobClosed',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
];
const DRAYAGE_COLUMNS = [
    // date(deliver_by), shipment ID, POD verification, origin, destination, equipment, weight, driver, POD, total, closed date
    {
        title: 'DELIVER_BY',
        field: 'jobDeliverBy',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
    {
        title: 'SHIPMENT_ID',
        field: 'shipment_id',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LINK, item.shipmentId);
        },
    },
    {
        title: 'POD_VERIFICATION',
        field: 'is_pod_valid',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.VERIFY_ICON);
        },
    },
    {
        title: 'ORIGIN',
        titleize: true,
        field: 'origin',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LOCATION);
        },
    },
    {
        title: 'DESTINATION',
        titleize: true,
        field: 'destination',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.LOCATION);
        },
    },
    {
        title: 'EQUIPMENT',
        field: 'equipments',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.EQUIPMENT);
        },
    },
    {
        title: 'WEIGHT',
        titleize: true,
        field: 'weight',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DEFAULT);
        },
    },
    {
        title: 'DRIVER',
        titleize: true,
        count: 1,
        field: 'driverName',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DEFAULT);
        },
    },
    {
        title: 'TOTAL',
        field: 'total',
        value(_item, total) {
            var _a;
            return getValue(total, VALUE_TYPES.MONEY, '', (_a = _item.lineItems[0]) === null || _a === void 0 ? void 0 : _a.currency);
        },
    },
    {
        title: 'POD',
        field: 'pod',
        value(item) {
            return React.createElement(JobPODColumn, { job: item });
        },
    },
    {
        title: 'CLOSED_DATE',
        field: 'jobClosed',
        value(item) {
            return getValue(item[this.field], VALUE_TYPES.DATE);
        },
    },
];
const COLUMNS = {
    shorthaul: SHORTHAUL_COLUMNS,
    drayage: DRAYAGE_COLUMNS,
};
module.exports = COLUMNS;
